import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import PhonePrefixSelect from '../components/PhonePrefixSelect';
import { detectUserPrefix } from '../components/detectUserPrefix';
import { BookOpen, Mail, Lock, User, Phone, Eye, EyeOff, Heart, Sparkles } from 'lucide-react';
import { BillingFieldsSection } from '../components/BillingFieldsSection';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WelcomePage = () => {
  const { register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefilledEmail = searchParams.get('email') || '';
  const pendingCoupon = searchParams.get('coupon') || '';
  const [couponInfo, setCouponInfo] = useState(null);
  // Editable registration coupon — prefilled from the URL param.
  const [couponCode, setCouponCode] = useState((searchParams.get('coupon') || '').toUpperCase());
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: prefilledEmail,
    phone: '',
    phone_prefix: '+34',
    password: '',
    taxId: '',
    fiscalName: '',
    billingType: 'particular',
    address: '',
    postalCode: '',
    city: '',
    province: ''
  });

  // Auto-detect user's country prefix (browser language → IP geo).
  useEffect(() => {
    let cancelled = false;
    detectUserPrefix().then(prefix => {
      if (!cancelled && prefix) {
        setFormData(d => d.phone_prefix === '+34' ? { ...d, phone_prefix: prefix } : d);
      }
    });
    return () => { cancelled = true; };
  }, []);

  // Fetch coupon details (value, expiry) so we can show "10% de descuento"
  // alongside the code in the landing banner.
  useEffect(() => {
    if (!pendingCoupon) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/coupons/${pendingCoupon}/preview`);
        if (!cancelled) setCouponInfo(res.data);
      } catch {
        if (!cancelled) setCouponInfo(null);
      }
    })();
    return () => { cancelled = true; };
  }, [pendingCoupon]);

  // Live price preview for the pay-at-signup model (coupon aware, debounced).
  const [regQuote, setRegQuote] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const code = couponCode.trim();
        const url = `${API_URL}/api/public/registration-quote${code ? `?coupon_code=${encodeURIComponent(code)}` : ''}`;
        const res = await axios.get(url);
        if (!cancelled) setRegQuote(res.data);
      } catch {
        if (!cancelled) setRegQuote(null);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [couponCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fullPhone = formData.phone ? `${formData.phone_prefix} ${formData.phone}` : '';
      const billing = {
        billing_type: formData.billingType,
        tax_id: formData.taxId.trim(),
        fiscal_name: formData.billingType === 'empresa' ? formData.fiscalName.trim() : '',
        address: formData.address.trim(),
        postal_code: formData.postalCode.trim(),
        city: formData.city.trim(),
        province: formData.province.trim(),
      };
      const res = await register(formData.email, formData.password, formData.firstName, formData.lastName, fullPhone, couponCode.trim().toUpperCase(), billing);
      if (res?.payment_pending && res?.checkout_url) {
        toast.success('¡Cuenta creada! Te llevamos al pago seguro…');
        window.location.href = res.checkout_url;
        return;
      }
      toast.success('¡Cuenta creada! Empecemos con tu libro.');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se ha podido completar el registro');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Intro */}
          <div className="text-center mb-10 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-3 py-1 rounded-full text-xs font-medium mb-4">
              <Sparkles className="w-3.5 h-3.5" />
              Empieza tu libro personalizado
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
              Convierte sus recuerdos en un regalo inolvidable
            </h1>
            <p className="mt-4 text-base sm:text-lg text-muted-foreground">
              Crea una cuenta y empieza el libro de la persona a la que quieres sorprender.
              Mira el video para ver lo fácil que es.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            {/* LEFT: Registration form */}
            <Card className="card-elevated order-2 lg:order-1" data-testid="welcome-register-card">
              <CardContent className="p-6 sm:p-8 space-y-5">
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center shadow-sm"
                    aria-hidden="true"
                  >
                    <BookOpen className="w-5 h-5" style={{ color: '#FFCC00' }} strokeWidth={1.8} />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl font-bold">Crea tu cuenta</h2>
                    <p className="text-xs text-muted-foreground">Solo te llevará un minuto</p>
                  </div>
                </div>

                {pendingCoupon && (
                  <div
                    className="rounded-md border border-amber-300 bg-amber-50 px-3 py-3"
                    data-testid="welcome-coupon-banner"
                  >
                    <div className="flex items-center gap-2 text-amber-900">
                      <Heart className="w-4 h-4 flex-shrink-0" />
                      <span className="text-sm font-semibold">Tienes un cupón de regalo</span>
                    </div>
                    <div className="mt-2 flex items-baseline gap-2 flex-wrap">
                      {couponInfo && couponInfo.type === 'percentage' && (
                        <span className="text-3xl font-extrabold text-amber-900 leading-none" data-testid="welcome-coupon-value">
                          {couponInfo.value}%
                        </span>
                      )}
                      {couponInfo && couponInfo.type === 'fixed' && (
                        <span className="text-3xl font-extrabold text-amber-900 leading-none" data-testid="welcome-coupon-value">
                          {couponInfo.value}€
                        </span>
                      )}
                      <span className="text-sm text-amber-900 font-medium">
                        de descuento
                      </span>
                    </div>
                    <div className="mt-2 text-xs text-amber-900 font-mono bg-amber-100 inline-block px-2 py-1 rounded border border-amber-300">
                      {pendingCoupon}
                    </div>
                    <p className="mt-2 text-xs text-amber-800 leading-relaxed">
                      Se aplicará automáticamente al total de tu compra al registrarte — verás el descuento reflejado en el precio.
                    </p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4" data-testid="welcome-register-form">
                  {regQuote && (
                    <div className="p-3 rounded-md border border-teal-200 bg-teal-50/60" data-testid="registration-price-box">
                      <p className="text-xs font-semibold text-teal-900 mb-1.5">Tu libro personalizado</p>
                      <div className="space-y-0.5 text-xs text-teal-900">
                        <div className="flex justify-between"><span>Libro base (1 ejemplar, envío España incluido)</span><span>{regQuote.base_price_eur.toFixed(2)}€</span></div>
                        {regQuote.discount_eur > 0 && (
                          <div className="flex justify-between text-emerald-700 font-medium"><span>{regQuote.discount_description || 'Descuento'}</span><span>−{regQuote.discount_eur.toFixed(2)}€</span></div>
                        )}
                        <div className="flex justify-between"><span>IVA (21%)</span><span>{regQuote.tax_eur.toFixed(2)}€</span></div>
                        <div className="flex justify-between border-t border-teal-200 pt-1 mt-1 text-sm font-bold"><span>Total al registrarte</span><span data-testid="registration-total">{regQuote.total_eur.toFixed(2)}€</span></div>
                      </div>
                      {regQuote.coupon_valid === false && (
                        <p className="text-[11px] text-red-600 mt-1.5 font-medium" data-testid="coupon-invalid-msg">
                          ⚠️ {regQuote.coupon_error || 'El cupón no es válido.'} El precio mostrado no incluye descuento.
                        </p>
                      )}
                      {regQuote.total_eur === 0 ? (
                        <p className="text-[11px] text-emerald-700 mt-1.5 font-medium">🎉 Con tu cupón, el registro es gratuito — sin pago.</p>
                      ) : (
                        <p className="text-[11px] text-teal-800 mt-1.5">Pagarás de forma segura con tarjeta (Stripe) al crear la cuenta. Los ejemplares adicionales se pagan al confirmar el pedido.</p>
                      )}
                    </div>
                  )}
                  <div>
                    <Label htmlFor="welcome-coupon" className="input-label">¿Tienes un cupón? <span className="text-muted-foreground font-normal">(opcional)</span></Label>
                    <Input
                      id="welcome-coupon"
                      type="text"
                      className="input-field font-mono uppercase tracking-wide"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      placeholder="Ej: BIENVENIDO20"
                      autoComplete="off"
                      data-testid="welcome-coupon-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="welcome-firstName" className="input-label">
                        <User className="w-4 h-4 inline mr-2" /> Nombre
                      </Label>
                      <Input
                        id="welcome-firstName"
                        type="text"
                        className="input-field"
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        placeholder="Ejem: Juan"
                        required
                        data-testid="welcome-firstname"
                      />
                    </div>
                    <div>
                      <Label htmlFor="welcome-lastName" className="input-label">Apellidos</Label>
                      <Input
                        id="welcome-lastName"
                        type="text"
                        className="input-field"
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        placeholder="Ejem: García"
                        required
                        data-testid="welcome-lastname"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="welcome-email" className="input-label">
                      <Mail className="w-4 h-4 inline mr-2" /> Email
                    </Label>
                    <Input
                      id="welcome-email"
                      type="email"
                      className="input-field"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="ejemplo@email.com"
                      required
                      data-testid="welcome-email"
                    />
                  </div>

                  <div>
                    <Label htmlFor="welcome-phone" className="input-label">
                      <Phone className="w-4 h-4 inline mr-2" /> Teléfono
                    </Label>
                    <div className="flex gap-2">
                      <PhonePrefixSelect
                        value={formData.phone_prefix || '+34'}
                        onValueChange={(v) => setFormData({ ...formData, phone_prefix: v })}
                        testId="welcome-phone-prefix"
                      />
                      <Input
                        id="welcome-phone"
                        type="tel"
                        className="input-field flex-1"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="612 345 678"
                        required
                        data-testid="welcome-phone"
                      />
                    </div>
                  </div>

                  <BillingFieldsSection formData={formData} setFormData={setFormData} />

                  <div>
                    <Label htmlFor="welcome-password" className="input-label">
                      <Lock className="w-4 h-4 inline mr-2" /> Contraseña
                    </Label>
                    <div className="relative">
                      <Input
                        id="welcome-password"
                        type={showPassword ? 'text' : 'password'}
                        className="input-field pr-10"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required
                        minLength={6}
                        data-testid="welcome-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full btn-primary"
                    disabled={loading}
                    data-testid="welcome-submit"
                  >
                    {loading ? 'Creando cuenta…' : 'Crear cuenta y empezar'}
                  </Button>
                </form>

                <div className="relative my-2">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-muted" />
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-card px-2 text-muted-foreground">o continúa con</span>
                  </div>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => loginWithGoogle?.()}
                  data-testid="welcome-google"
                >
                  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Google
                </Button>

                <p className="text-center text-xs text-muted-foreground">
                  ¿Ya tienes cuenta? <Link to="/login" className="text-primary font-medium hover:underline">Entra aquí</Link>
                </p>
              </CardContent>
            </Card>

            {/* RIGHT: Hero family image */}
            <div className="order-1 lg:order-2 lg:sticky lg:top-28 space-y-4">
              <div
                className="relative aspect-video rounded-xl overflow-hidden border border-primary/15 shadow-md"
                data-testid="welcome-hero-image"
              >
                <img
                  src="/hero-family.jpg"
                  alt="Familia compartiendo historias junto a la chimenea"
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="eager"
                  decoding="async"
                />
                {/* Subtle bottom gradient so the caption stays readable
                    over the warm photo without dimming the firelight. */}
                <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/45 via-black/15 to-transparent pointer-events-none" />
                <div className="absolute bottom-3 left-4 right-4 text-white">
                  <p className="text-sm font-semibold drop-shadow-md">
                    Las mejores historias se cuentan en familia
                  </p>
                  <p className="text-xs opacity-90 drop-shadow">
                    Conviértelas en un libro para guardarlas para siempre
                  </p>
                </div>
              </div>

              <div className="grid sm:grid-cols-3 gap-3">
                <FeatureBullet n="1" title="Comparte el formulario" desc="Tus seres queridos rellenan el formulario en minutos." />
                <FeatureBullet n="2" title="Nuestro equipo lo redacta" desc="Convertimos sus aportaciones en un libro personalizado." />
                <FeatureBullet n="3" title="Lo recibes en casa" desc="Tapa dura, calidad de imprenta, listo para regalar." />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const FeatureBullet = ({ n, title, desc }) => (
  <div className="flex gap-3 items-start p-3 rounded-md bg-card border">
    <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0 text-xs font-bold">
      {n}
    </div>
    <div>
      <p className="text-sm font-semibold leading-tight">{title}</p>
      <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{desc}</p>
    </div>
  </div>
);

export default WelcomePage;
